import { useMemo } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';

import { CardLink } from 'components/Links';
import { Box } from 'components/Box';
import { Breadcrumbs } from 'components/Breadcrumbs';
import {
  Details,
  BlogCardContainer,
  ImageContainer,
  Description,
  TextContainer,
  StyledHeader,
} from './styled';
import { getDetails } from '../getDetails';

import type { FC } from 'react';
import type { BlogPost } from 'types/blog/blogPost';

interface BlogCardProps {
  entry: BlogPost;
  isHero?: boolean;
}

/** A card component that previews and links to either a post from either our blog or partner page */
export const BlogCard: FC<BlogCardProps> = ({ entry, isHero = false }) => {
  const details = getDetails({ author: entry.author, date: entry.date });

  const image = useMemo(() => {
    if (entry.image.gatsbyImageData !== null) {
      return (
        <GatsbyImage
          image={entry.image.gatsbyImageData!}
          alt={entry.image.title || '../images/etf-tracker/hero.png'}
          objectFit="contain"
        />
      );
    }

    return entry.image.staticImage || null;
  }, [entry.image.staticImage, entry.image.gatsbyImageData, entry.image.title]);

  return (
    <CardLink to={entry.urlSlug}>
      <BlogCardContainer isHero={isHero}>
        <ImageContainer isHero={isHero}>
          <Box marginBottom={6}>{image}</Box>
        </ImageContainer>

        <TextContainer isHero={isHero}>
          <Breadcrumbs crumbs={entry.categories} />

          <StyledHeader as={isHero ? 'h2' : 'h3'}>{entry.title}</StyledHeader>

          {details && <Details>{details}</Details>}
          {entry.description && <Description>{entry.description}</Description>}
        </TextContainer>
      </BlogCardContainer>
    </CardLink>
  );
};
